
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/auth/AuthContext';
import { menuItems } from '../AppSidebar';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onMenuItemClick: () => void;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({
  isOpen,
  onClose,
  onMenuItemClick
}) => {
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth();

  return (
    <div className={`fixed top-0 left-0 h-full w-80 bg-background border-r z-50 transform transition-transform duration-300 ease-in-out ${
      isOpen ? "translate-x-0" : "-translate-x-full"
    }`}>
      <div className="flex flex-col h-full">
        {/* Mobile sidebar header */}
        <div className="flex items-center justify-between p-4 border-b h-16">
          <Link to="/dashboard" className="flex items-center" onClick={onMenuItemClick}>
            <img 
              src="/lovable-uploads/924a6ca1-11fb-46d9-a66b-f6b94a0c23c6.png" 
              alt="ProptPilot Logo" 
              className="h-8 w-auto max-w-[120px]"
              style={{ objectFit: 'contain' }}
            />
          </Link>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-10 w-10 p-0"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Mobile navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onMenuItemClick}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors w-full ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-accent text-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Admin menu item for mobile */}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={onMenuItemClick}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors w-full ${
                  location.pathname === '/admin'
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-accent text-foreground'
                }`}
              >
                <span className="h-5 w-5 flex-shrink-0">🛡️</span>
                <span className="font-medium">Admin</span>
              </Link>
            )}
          </div>
        </nav>
        
        {/* User info at bottom of mobile sidebar */}
        {currentUser && (
          <div className="p-4 border-t">
            <div className="text-sm text-muted-foreground">
              <div className="truncate max-w-[280px]">
                {currentUser.email}
              </div>
              {isAdmin && (
                <div className="text-amber-600 text-xs mt-1">(Admin)</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSidebar;
